import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading2,
  Code,
  Undo,
  Redo,
} from 'lucide-react'
import { Toggle } from '@/components/ui/toggle'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import styles from './styles.module.css'
import type { ToolbarProps } from './types'

export function Toolbar({ editor, disabled }: ToolbarProps) {
  if (!editor) return null

  return (
    <div className={styles.toolbar}>
      <Toggle
        size='sm'
        pressed={editor.isActive('bold')}
        onPressedChange={() => editor.chain().focus().toggleBold().run()}
        disabled={disabled}
        variant='outline'
        className={cn(
          'h-8 w-8 p-0',
          'data-[state=on]:bg-muted data-[state=on]:text-muted-foreground'
        )}
      >
        <Bold className='w-4 h-4' />
      </Toggle>

      <Toggle
        size='sm'
        pressed={editor.isActive('italic')}
        onPressedChange={() => editor.chain().focus().toggleItalic().run()}
        disabled={disabled}
        variant='outline'
        className={cn(
          'h-8 w-8 p-0',
          'data-[state=on]:bg-muted data-[state=on]:text-muted-foreground'
        )}
      >
        <Italic className='w-4 h-4' />
      </Toggle>

      <Toggle
        size='sm'
        pressed={editor.isActive('heading', { level: 2 })}
        onPressedChange={() =>
          editor.chain().focus().toggleHeading({ level: 2 }).run()
        }
        disabled={disabled}
        variant='outline'
        className={cn(
          'h-8 w-8 p-0',
          'data-[state=on]:bg-muted data-[state=on]:text-muted-foreground'
        )}
      >
        <Heading2 className='w-4 h-4' />
      </Toggle>

      <Separator orientation='vertical' className='h-6 mx-1' />

      <Toggle
        size='sm'
        pressed={editor.isActive('bulletList')}
        onPressedChange={() => editor.chain().focus().toggleBulletList().run()}
        disabled={disabled}
        variant='outline'
        className={cn(
          'h-8 w-8 p-0',
          'data-[state=on]:bg-muted data-[state=on]:text-muted-foreground'
        )}
      >
        <List className='w-4 h-4' />
      </Toggle>

      <Toggle
        size='sm'
        pressed={editor.isActive('orderedList')}
        onPressedChange={() => editor.chain().focus().toggleOrderedList().run()}
        disabled={disabled}
        variant='outline'
        className={cn(
          'h-8 w-8 p-0',
          'data-[state=on]:bg-muted data-[state=on]:text-muted-foreground'
        )}
      >
        <ListOrdered className='w-4 h-4' />
      </Toggle>

      <Toggle
        size='sm'
        pressed={editor.isActive('code')}
        onPressedChange={() => editor.chain().focus().toggleCode().run()}
        disabled={disabled}
        variant='outline'
        className={cn(
          'h-8 w-8 p-0',
          'data-[state=on]:bg-muted data-[state=on]:text-muted-foreground'
        )}
      >
        <Code className='w-4 h-4' />
      </Toggle>

      <div className='flex gap-1 ml-auto'>
        <Toggle
          size='sm'
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo() || disabled}
          variant='outline'
          className='w-8 h-8 p-0'
        >
          <Undo className='w-4 h-4' />
        </Toggle>

        <Toggle
          size='sm'
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo() || disabled}
          variant='outline'
          className='w-8 h-8 p-0'
        >
          <Redo className='w-4 h-4' />
        </Toggle>
      </div>
    </div>
  )
}
